import { createSignal, type Accessor } from 'solid-js'
import type { ItemType } from '~/services/bootstrap'
import {
  deriveStarterPackCandidate,
  hydrateStarterPack,
  refetchStarterPack,
  type StarterPack,
  type StarterPackCandidate,
  type StarterPackSource,
} from '~/services/starter-pack'
import { gateStart, type HydrationGate } from './catalogue-grid'

// The starter pack's hero slot (GLOSSARY.md "Starter pack (新手禮包)"): created
// with the other hero slots, so nothing is inserted later (ADR 0024), pending
// while its purchase stats and native price resolve, then hydrated or
// omitted. It leads the hero row until owned, trails it after, and, on a
// Featured landing, belongs to the reveal surface — the reveal gate reads its
// status beside the main-product slots'.
export interface StarterPackSlot {
  // The pre-hydration facts — banner, deadline, contents — known from
  // bootstrap, so the card's banner starts loading behind its pending bars.
  candidate: StarterPackCandidate
  // Present once hydrated: ownership and the Shop IAP price.
  pack?: StarterPack
  status: 'hydrated' | 'omitted' | 'pending'
}

export interface StarterPackState {
  // Records a committed purchase of productId (GLOSSARY.md "Starter pack
  // (新手禮包)"): when it is the starter pack, the slot turns owned at once
  // and stays owned — a failed or lagging post-purchase refetch never leaves
  // a just-bought pack buyable. Any other product is ignored.
  markBought: (productId: number) => void
  // Re-reads the starter pack after a committed purchase (GLOSSARY.md
  // "Starter pack (新手禮包)"). On success the slot takes the result: a newly
  // eligible pack arrives hydrated (never as a pending skeleton), a bought
  // one turns owned, one the server no longer returns goes. A failed refetch
  // records diagnostics and leaves the slot as it is. The promise settles —
  // never rejects — once this refetch has applied or been dropped; when
  // refetches overlap, the latest one started wins.
  refetch: () => Promise<void>
  // True while any refetch is in flight.
  refetching: Accessor<boolean>
  // undefined = no starter-pack slot: the collection was missing, empty, or
  // held nothing valid.
  slot: Accessor<StarterPackSlot | undefined>
}

// Pending, the slot leads the row — its ownership is still unknown — so the
// hero-row gate judges it as display index 0: behind the boot skeleton on a
// Featured landing, after the Shop reveal otherwise (GLOSSARY.md "Shop
// hydration order").
export function createStarterPack(
  source: StarterPackSource,
  userId: number,
  options?: { gate?: HydrationGate },
): StarterPackState {
  const candidate = deriveStarterPackCandidate(source)
  const [slot, setSlot] = createSignal<StarterPackSlot | undefined>(
    candidate && { candidate, status: 'pending' },
  )
  // Bootstrap hydration and every refetch are numbered in start order; a
  // result applies only over an older one, so a slow earlier lookup never
  // overwrites a newer answer.
  let started = 0
  let applied = 0
  // Starter-pack products bought this session: owned whatever a later
  // lookup says.
  const bought = new Set<number>()
  const withBought = (next: StarterPackSlot | undefined) => {
    const pack = next?.pack
    if (!next || !pack || pack.owned || !bought.has(pack.product.id)) {
      return next
    }
    return { ...next, pack: { ...pack, owned: true } }
  }
  const apply = (id: number, next: StarterPackSlot | undefined) => {
    if (id < applied) return
    applied = id
    setSlot(withBought(next))
  }
  const markBought = (productId: number) => {
    bought.add(productId)
    setSlot(withBought)
  }

  if (candidate) {
    const id = ++started
    const run = () =>
      hydrateStarterPack(candidate, userId)
        // hydrateStarterPack is total; this backstop settles the slot on a
        // programming error so the reveal gate cannot stall on it.
        .catch(() => null)
        .then((pack) =>
          apply(
            id,
            pack
              ? { candidate, pack, status: 'hydrated' }
              : { candidate, status: 'omitted' },
          ),
        )
    const gate = options?.gate
    const start = gate && gateStart(gate, 0)
    if (start) void start.then(run)
    else void run()
  }

  // The item types Shop holds, so a refetch asks only for missing ones.
  const knownItemTypes: ItemType[] = [...source.itemTypes]
  const [inFlight, setInFlight] = createSignal(0)
  const refetch = async () => {
    const id = ++started
    setInFlight((count) => count + 1)
    try {
      const result = await refetchStarterPack(userId, knownItemTypes)
        // refetchStarterPack is total; a programming error keeps the state
        // like any failed refetch.
        .catch(() => null)
      if (!result) return
      knownItemTypes.push(...result.itemTypes)
      const pack = result.pack
      apply(id, pack && { candidate: pack, pack, status: 'hydrated' })
    } finally {
      setInFlight((count) => count - 1)
    }
  }

  return { markBought, refetch, refetching: () => inFlight() > 0, slot }
}
